import { useEffect } from 'react';

import { toast } from '@/components/ui/toast';

import { coinKey, useTradingStore } from '../store';

/**
 * Fills a DEMO limit order when the streamed price crosses it.
 *
 * Demo mode has no exchange, so nothing else would ever fill the order — but it
 * does stream the same live prices as real mode (see the CLAUDE.md note: demo
 * is not a price simulator), which is exactly what a resting order needs. A
 * long fills when the price comes DOWN to the limit, a short when it comes UP:
 * the same side rule Hyperliquid applies.
 *
 * Only the charted market is watched, because only the charted market has a
 * price on this client. An order on another market rests until the user looks
 * at it, which is a demo-mode limitation, not a rule of the feature.
 *
 * Demo TP/SL are still not simulated: a filled order becomes the ordinary demo
 * position, which the user closes by hand.
 */
export function useDemoLimitFills(
    currentValue: number,
    currentValueCoin: string | null,
): void {
    useEffect(() => {
        const s = useTradingStore.getState();
        if (s.runtimeMode === 'real') return;
        if (!(currentValue > 0)) return;
        // the price must belong to the market it is being measured against —
        // the spring is still crossing from the previous market otherwise.
        const key = coinKey(currentValueCoin);
        if (key === null || key !== coinKey(s.selectedCoin)) return;
        const order = s.demoRestingOrders[key];
        if (!order) return;
        // demo holds one position at a time; the order waits its turn.
        if (s.activePosition) return;

        const crossed =
            order.type === 'LONG'
                ? currentValue <= order.limitPrice
                : currentValue >= order.limitPrice;
        if (!crossed) return;

        s.fillDemoRestingOrder(key);
        toast.success('Limit order filled, position is running', {
            duration: 8000,
        });
    }, [currentValue, currentValueCoin]);
}
